import type { CheckoutOrderDetailsDto } from '@/services/orders.service';

type PromoCodeInfo = CheckoutOrderDetailsDto['promoCodeInfo'];

export type PromoPricingItem = {
  unitPrice: number;
  domainId: number;
  discountAmount?: number;
};

type PromoScope = ReturnType<typeof buildPromoScope>;

const MIN_CHECKOUT_TOTAL_CENTS = 50;

function buildPromoScope(promoCodeInfo: PromoCodeInfo) {
  const isPromoActive = promoCodeInfo?.status === 'active';
  const isPromoPossible = promoCodeInfo?.status === 'active' || promoCodeInfo?.status === 'inactive';
  const discountType = promoCodeInfo?.type;
  const discountValue = Math.max(0, Math.trunc(promoCodeInfo?.value ?? 0));
  const minOrderAmountInCents =
    promoCodeInfo?.minOrderAmount != null
      ? Math.max(0, Math.round(promoCodeInfo.minOrderAmount * 100))
      : null;
  const applicableDomainIds = new Set(
    Array.isArray(promoCodeInfo?.restrictedDomainIds) ? promoCodeInfo.restrictedDomainIds : []
  );
  const hasApplicableDomainScope = applicableDomainIds.size > 0;

  return {
    isPromoActive,
    isPromoPossible,
    discountType,
    discountValue,
    minOrderAmountInCents,
    applicableDomainIds,
    hasApplicableDomainScope,
  };
}

function isPromoApplicableByTotal(scope: PromoScope, subtotalInCents: number): boolean {
  if (scope.minOrderAmountInCents == null) return true;
  return subtotalInCents >= scope.minOrderAmountInCents;
}

function calculateEligibleSubtotalInCents(scope: PromoScope, items: PromoPricingItem[]): number {
  if (!scope.hasApplicableDomainScope) {
    return items.reduce((sum, item) => sum + item.unitPrice, 0);
  }

  return items.reduce((sum, item) => {
    if (!scope.applicableDomainIds.has(item.domainId)) return sum;
    return sum + item.unitPrice;
  }, 0);
}

export function isPromoApplicableToDomain(promoCodeInfo: PromoCodeInfo, domainId: number): boolean {
  const { hasApplicableDomainScope, applicableDomainIds } = buildPromoScope(promoCodeInfo);
  //console.log(`Checking isPromoApplicableToDomain`, !hasApplicableDomainScope || applicableDomainIds.has(domainId));
  return !hasApplicableDomainScope || applicableDomainIds.has(domainId);
}

function calculatePercentageDiscountForItemWithScope(
  scope: PromoScope,
  promoCodeInfo: PromoCodeInfo,
  unitPrice: number,
  domainId: number,
  subtotalInCents: number
): number {
  if (!scope.isPromoPossible || scope.discountType !== 'percentage') return 0;
  if (!isPromoApplicableByTotal(scope, subtotalInCents)) return 0;
  if (!isPromoApplicableToDomain(promoCodeInfo, domainId)) return 0;
  //console.log(`Applying ${scope.discountValue}% discount to item with price ${unitPrice} in domain ${domainId}`);
  return Math.round((unitPrice * scope.discountValue) / 100);
}

export function calculatePercentageDiscountForItem(
  promoCodeInfo: PromoCodeInfo,
  unitPrice: number,
  domainId: number,
  subtotalInCents = unitPrice
): number {
  const scope = buildPromoScope(promoCodeInfo);
  return calculatePercentageDiscountForItemWithScope(
    scope,
    promoCodeInfo,
    unitPrice,
    domainId,
    subtotalInCents
  );
}

export function calculatePromoTotals(items: PromoPricingItem[], promoCodeInfo: PromoCodeInfo) {
  const scope = buildPromoScope(promoCodeInfo);
  const { isPromoActive, isPromoPossible, discountType, discountValue } = scope;

  const subtotal = items.reduce((sum, item) => sum + item.unitPrice, 0);
  const eligibleSubtotal = calculateEligibleSubtotalInCents(scope, items);
  const promoApplicableByTotal = isPromoApplicableByTotal(scope, eligibleSubtotal);
  let grandTotal = 0;
  let promoDiscountAmount = 0;
  let fixedEligibleTotal = 0;

  for (const item of items) {
    const isApplicable = isPromoApplicableToDomain(promoCodeInfo, item.domainId);
    const lineDiscount = promoApplicableByTotal
      ? (item.discountAmount ??
        calculatePercentageDiscountForItemWithScope(
          scope,
          promoCodeInfo,
          item.unitPrice,
          item.domainId,
          eligibleSubtotal
        ))
      : 0;

    if (
      promoApplicableByTotal &&
      (isPromoPossible || isPromoActive) &&
      discountType === 'fixed' &&
      isApplicable
    ) {
      fixedEligibleTotal += item.unitPrice;
    } else {
      promoDiscountAmount += lineDiscount;
    }

    grandTotal += Math.max(0, item.unitPrice - lineDiscount);
  }

  if ((isPromoActive || isPromoPossible) && discountType === 'fixed' && discountValue > 0) {
    const fixedDiscount = Math.min(fixedEligibleTotal, discountValue * 100);
    promoDiscountAmount += fixedDiscount;
    grandTotal = Math.max(0, subtotal - promoDiscountAmount);
  }

  if (items.length > 0 && grandTotal < MIN_CHECKOUT_TOTAL_CENTS) {
    grandTotal = MIN_CHECKOUT_TOTAL_CENTS;
    promoDiscountAmount = Math.max(0, subtotal - grandTotal);
  }

  return {
    subtotal,
    grandTotal,
    promoDiscountAmount,
  };
}

export function resolvePromoDomainCoverage(
  items: Array<{ domainId: number }>,
  promoCodeInfo: PromoCodeInfo
): { domainRestrictionPartial: boolean; domainRestrictionInvalid: boolean } {
  const restrictedDomainIds = Array.isArray(promoCodeInfo?.restrictedDomainIds)
    ? promoCodeInfo.restrictedDomainIds.filter((id): id is number => Number.isInteger(id))
    : [];

  if (restrictedDomainIds.length === 0) {
    return { domainRestrictionPartial: false, domainRestrictionInvalid: false };
  }

  const selectedDomainIds = [...new Set(
    items
      .map((item) => item.domainId)
      .filter((id): id is number => Number.isInteger(id))
  )];

  if (selectedDomainIds.length === 0) {
    return { domainRestrictionPartial: false, domainRestrictionInvalid: false };
  }

  const matchesAnyDomain = selectedDomainIds.some((domainId) => restrictedDomainIds.includes(domainId));

  if (!matchesAnyDomain) {
    return { domainRestrictionPartial: false, domainRestrictionInvalid: true };
  }

  const hasOutOfScopeDomains = selectedDomainIds.some(
    (domainId) => !restrictedDomainIds.includes(domainId)
  );

  return {
    domainRestrictionPartial: hasOutOfScopeDomains,
    domainRestrictionInvalid: false,
  };
}